import { NgClass } from '@angular/common';
import { Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { LoaderComponent } from '@joii/design-system';
import * as Sentry from '@sentry/angular';
import { StaffOnlineWriteService } from 'src/pms/shared/services/staff-online-write.service/staff-online-write.service';
import { VetAIHeaderComponent } from 'src/shared/components/design/vetai-header/vetai-header.component';
import { VetAIHeader, VetAIHeaderItemType } from 'src/shared/components/design/vetai-header/vetai-header.interface';
import { MaintenanceWindowComponent } from 'src/shared/components/maintenance-window/maintenance-window.component';
import { AuthStateService } from 'src/shared/services/auth-state/auth-state.service';
import { DarkModeService } from 'src/shared/services/darkmode/darkmode_service';
import { initTranslations } from 'src/shared/translate/custom.translate.loader';
import { environment } from '../environments/environment';
import { StaffProfileStore } from './stores/staff-profile/staff-profile.store';
import { getPmsDashboardPath } from './utils/pms-dashboard-path.util';

import {
  LucideCalendar,
  LucideClipboard,
  LucideRotateCcwClock,
  LucideHouse,
  LucidePhone,
  LucideUser,
  LucideVideo,
} from '@lucide/angular';
@Component({
  selector: 'app-root',
  templateUrl: './pms.component.html',
  providers: [StaffProfileStore, StaffOnlineWriteService],
  imports: [RouterOutlet, VetAIHeaderComponent, NgClass, LoaderComponent, MaintenanceWindowComponent],
})
export class PMSComponent {
  darkModeService: DarkModeService = inject(DarkModeService);
  authStateService: AuthStateService = inject(AuthStateService);
  private readonly staffOnlineWriteService = inject(StaffOnlineWriteService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  readonly appShellLoading = signal(true);
  menu: VetAIHeader;
  private lastReportedDeauthReason: string | null = null;

  authChangedEffect = effect(() => {
    const deauthReason = this.authStateService.deauthReason();
    const deauthedStaffUser = this.authStateService.deauthedStaffUser();
    const isAuthReady = this.authStateService.isAuthReady();

    if (isAuthReady) {
      if (deauthReason) {
        if (this.lastReportedDeauthReason !== deauthReason) {
          Sentry.captureMessage('Staff authentication revoked', {
            level: 'warning',
            tags: {
              'staff-deauth': 'true',
              deauth_reason: deauthReason,
            },
            user: deauthedStaffUser
              ? {
                  id: deauthedStaffUser.id,
                  email: deauthedStaffUser.email ?? undefined,
                  username: deauthedStaffUser.username ?? undefined,
                }
              : undefined,
          });
          this.lastReportedDeauthReason = deauthReason;
          this.authStateService.clearDeauthReason();
        }
      } else {
        this.lastReportedDeauthReason = null;
      }
    }
  });

  constructor() {
    initTranslations();

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (!this.appShellLoading()) {
        return;
      }
      if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        this.appShellLoading.set(false);
      }
    });

    this.menu = {
      hiddenpaths: [
        '/login',
        '/register',
        '/unauthorised',
        '/forgottenpassword',
        '/notfound',
        '/checkaccess',
        '/consultation/',
        '/onboarding/staff',
        '/aiReview',
        '/trial-consultation',
        '/not-clinician',
        '/maintenance-window',
      ],
      logo: 'https://firebasestorage.googleapis.com/v0/b/vetai-e3e47.appspot.com/o/joii%2Fvetaiicon.png?alt=media&token=3e3942bd-4fd8-48c6-9249-096d5b35bef9',
      accountpath: '/account/staffprofile',
      items: [
        {
          id: getPmsDashboardPath(),
          title: 'Home',
          type: VetAIHeaderItemType.Path,
          requiredroles: [],
          icon: LucideHouse,
          dataautomationid: 'pms-sidebar-dashboard',
          hidden: false,
        },
        {
          id: '/history',
          title: 'History',
          type: VetAIHeaderItemType.Path,
          requiredroles: [],
          icon: LucideRotateCcwClock,
          dataautomationid: 'pms-sidebar-consultation-history',
          hidden: false,
        },
        {
          id: '/calendar',
          title: 'Calendar',
          type: VetAIHeaderItemType.Path,
          requiredroles: [],
          icon: LucideCalendar,
          dataautomationid: 'pms-sidebar-calendar',
          hidden: false,
        },
        {
          id: '/account/staffprofile',
          title: 'Profile',
          type: VetAIHeaderItemType.Path,
          requiredroles: [],
          icon: LucideUser,
          dataautomationid: 'pms-sidebar-profile',
          hidden: false,
        },
        {
          id: '/videocheck',
          title: 'Test Video',
          type: VetAIHeaderItemType.Path,
          requiredroles: [],
          icon: LucideVideo,
          dataautomationid: 'pms-sidebar-test-video',
          hidden: false,
        },
        ...(environment.production
          ? []
          : [
              {
                id: '/testPhone',
                title: 'Test Phone',
                type: VetAIHeaderItemType.Path,
                requiredroles: [],
                icon: LucidePhone,
                dataautomationid: 'pms-sidebar-test-phone',
                hidden: false,
              },
            ]),
        {
          id: 'token-copier',
          title: 'Copy Auth',
          type: VetAIHeaderItemType.TokenCopier,
          requiredroles: [],
          icon: LucideClipboard,
          dataautomationid: 'pms-sidebar-token-copier',
          hidden: false,
        },
      ],
    };
  }
}
